import React, { useEffect, useRef, useState } from 'react';
import logoMp from '../img/mp-icono.png';
import './DemoTransferencias.css';

// Panel animado del login y del registro: el título se escribe solo y abajo van entrando
// transferencias como las ve un vendedor en el app (Home.js): hora, quién envía y monto, la
// última con borde verde. Los datos son de ejemplo. Con "reducir movimiento" queda quieto.
// Cuántas tarjetas se ven lo decide el CSS de cada página (--demo-transf-alto-lista).

const TITULO = 'Tus empleados confirman transferencias sin acceso a tu cuenta';
const PUNTOS = [
    ['Al instante.', 'Ven el monto y quién lo envía.'],
    ['Sin riesgos.', 'No ven el saldo ni pueden mover plata.'],
    ['Sin comprobantes falsos.', 'Si no está en la lista, no entró.'],
];

// null en nombre = transferencia desde una cuenta bancaria, como la muestra el app.
const EJEMPLOS = [
    { nombre: 'Lucía Fernández', monto: 22000 },
    { nombre: 'Diego Romero', monto: 35000 },
    { nombre: null, monto: 16900 },
    { nombre: 'Carolina Paz', monto: 32600 },
    { nombre: 'Javier Ledesma', monto: 31900 },
    { nombre: null, monto: 18500 },
    { nombre: 'Martina Sosa', monto: 9800 },
    { nombre: 'Tomás Acosta', monto: 47250 },
];

// Las que entran en el panel más alto (registro) y una más que sale por abajo.
const EN_LISTA = 5;
const MS_POR_LETRA = 32;
const MS_ENTRE_TRANSFERENCIAS = 2800;

const formatearMonto = (n) => `$${n.toLocaleString('es-AR')}`;
const formatearHora = (fecha) =>
    fecha.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

const reduceMovimiento = () =>
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Reloj de mentira: cada transferencia entra unos minutos después de la anterior, así la
// demo no muestra horas separadas por los 3 segundos reales entre una y otra.
const MINUTOS_ENTRE = [3, 7, 2, 5, 4, 6, 3, 8];
const SEGUNDOS = [35, 9, 2, 53, 26, 41, 17, 48];
const horaDe = (inicio, minutos, segundos) => {
    const fecha = new Date(inicio + minutos * 60000);
    fecha.setSeconds(segundos);
    return formatearHora(fecha);
};
const horaDeTransferencia = (inicio, i) => {
    let minutos = 0;
    for (let k = 0; k <= i; k += 1) minutos += MINUTOS_ENTRE[k % MINUTOS_ENTRE.length];
    return horaDe(inicio, minutos, SEGUNDOS[i % SEGUNDOS.length]);
};

// Arranca con transferencias "de hace un rato" para que la lista no aparezca vacía.
const listaInicial = (inicio) => [
    { ...EJEMPLOS[EJEMPLOS.length - 1], id: -1, hora: horaDe(inicio, -6, 12) },
    { ...EJEMPLOS[EJEMPLOS.length - 2], id: -2, hora: horaDe(inicio, -15, 57) },
    { ...EJEMPLOS[EJEMPLOS.length - 3], id: -3, hora: horaDe(inicio, -23, 31) },
];

// Lo tipeado hasta ahora, con la parte "resaltar" del título en naranja.
function TextoTipeado({ texto, letras, resaltar }) {
    const desde = resaltar ? texto.indexOf(resaltar) : -1;
    if (desde < 0) return texto.slice(0, letras);
    const hasta = desde + resaltar.length;
    return (
        <>
            {texto.slice(0, Math.min(letras, desde))}
            {letras > desde && <span className="demo-transf-resaltado">{texto.slice(desde, Math.min(letras, hasta))}</span>}
            {letras > hasta && texto.slice(hasta, letras)}
        </>
    );
}

function IconoBanco() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#001c4d" strokeWidth="1.8"
            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 10l9-6 9 6" />
            <line x1="5" y1="10" x2="5" y2="18" />
            <line x1="9.5" y1="10" x2="9.5" y2="18" />
            <line x1="14.5" y1="10" x2="14.5" y2="18" />
            <line x1="19" y1="10" x2="19" y2="18" />
            <line x1="3" y1="20" x2="21" y2="20" />
        </svg>
    );
}

function IconoReloj() {
    return (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"
            strokeLinecap="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 3" />
        </svg>
    );
}

function DemoTransferencias({ titulo = TITULO, resaltar = null, puntos = PUNTOS }) {
    const quieto = useRef(reduceMovimiento()).current;
    const [letras, setLetras] = useState(quieto ? titulo.length : 0);
    // La demo arranca media hora atrás, para que las horas no queden en el futuro enseguida.
    const inicio = useRef(Date.now() - 30 * 60000).current;
    const [transferencias, setTransferencias] = useState(() => listaInicial(inicio));
    const siguiente = useRef(0);

    const tituloCompleto = letras >= titulo.length;

    useEffect(() => {
        if (tituloCompleto) return undefined;
        const t = setTimeout(() => setLetras((n) => n + 1), MS_POR_LETRA);
        return () => clearTimeout(t);
    }, [letras, tituloCompleto]);

    useEffect(() => {
        if (quieto || !tituloCompleto) return undefined;
        const agregar = () => {
            // Con la pestaña oculta el navegador pausa las animaciones: si se siguieran
            // sumando, al volver entrarían varias tarjetas juntas.
            if (document.hidden) return;
            const i = siguiente.current;
            siguiente.current += 1;
            const ejemplo = EJEMPLOS[i % EJEMPLOS.length];
            setTransferencias((lista) =>
                [{ ...ejemplo, id: i, hora: horaDeTransferencia(inicio, i) }, ...lista].slice(0, EN_LISTA)
            );
        };
        const primera = setTimeout(agregar, 900);
        const intervalo = setInterval(agregar, MS_ENTRE_TRANSFERENCIAS);
        return () => {
            clearTimeout(primera);
            clearInterval(intervalo);
        };
    }, [quieto, tituloCompleto, inicio]);

    return (
        <>
            <h2 className="demo-transf-titulo">
                {/* El texto completo, invisible, reserva el alto final y evita que el panel salte. */}
                <span className="demo-transf-titulo-molde">
                    <TextoTipeado texto={titulo} letras={titulo.length} resaltar={resaltar} />
                </span>
                <span className="demo-transf-titulo-tipeado">
                    <TextoTipeado texto={titulo} letras={letras} resaltar={resaltar} />
                    {!tituloCompleto && <span className="demo-transf-cursor" />}
                </span>
            </h2>

            <ul className={`demo-transf-puntos${tituloCompleto ? ' visibles' : ''}`}>
                {puntos.map(([fuerte, resto]) => (
                    <li key={fuerte}><strong>{fuerte}</strong> {resto}</li>
                ))}
            </ul>

            <div className="demo-transf-app">
                <div className="demo-transf-app-barra">
                    <span>Transferencias recibidas</span>
                    <span className="demo-transf-vivo">En vivo</span>
                </div>
                <div className="demo-transf-lista">
                    {transferencias.map((t, i) => (
                        <div
                            key={t.id}
                            className={`demo-transf-tarjeta${t.id < 0 ? ' fija' : ''}${i === 0 && t.id >= 0 ? ' nueva' : ''}`}
                        >
                            <div className="demo-transf-izq">
                                <span className="demo-transf-hora"><IconoReloj />Hoy {t.hora}</span>
                                <span className="demo-transf-origen">
                                    {t.nombre ? <img src={logoMp} alt="" /> : <IconoBanco />}
                                    <strong>{t.nombre || 'Desde cuenta bancaria'}</strong>
                                </span>
                            </div>
                            <span className="demo-transf-monto">{formatearMonto(t.monto)}</span>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
}

export default DemoTransferencias;
